import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import CardItems from './card_item/card_items'

import './card.css'
import moment from 'moment'
import EmailField from './EmailField'
import { SHARED_WITH_TYPES, SIGNING_PROCESS, SIGNING_PROGRESS_TO_TEXT, VALIDATE_EMAIL_REGEX } from '../../../constants'
import SpmsProcessError from './process_error'
import { usePolyglot } from '../../../hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAddressCard, faPlus, faCircleCheck } from '@fortawesome/pro-regular-svg-icons'
import { encryptWithAmaKey } from '../../../utils/cmdCrypto'

const MAX_EMAILS = 4

/**
 * The API returns `sharedWith` as an array, while the redux-shaped prescription holds it as an
 * object keyed by id — the same duality already handled for `lines`. Normalises both shapes into
 * the list of e-mail addresses shared with the patient.
 */
function getSharedEmails (sharedWith) {
  if (!sharedWith) return []
  const entries = Array.isArray(sharedWith) ? sharedWith : Object.values(sharedWith)
  return entries
    .filter(entry => entry.type === SHARED_WITH_TYPES.EMAIL)
    .map(entry => entry.value)
}

function toEmailEntry (value) {
  return { value, valid: VALIDATE_EMAIL_REGEX.test(value), confirmed: true }
}

const CMD_STEP = {
  IDLE: 'IDLE',
  CREDENTIALS: 'CREDENTIALS',
  PROCESSING: 'PROCESSING',
  OTP: 'OTP',
  OTP_PROCESSING: 'OTP_PROCESSING',
}

const Card = React.memo(function Card ({
  collapsed: startCollapsed, number, items, shouldShowSignButton, prescription, addEmails, patientEmail,
  addEmailsAndEmitPrescription, getContactsForPrescription, initiateCmdSignature, completeCmdSignature,
  doctorClinicalName, patientFullName, cmdKeys
}) {
  const t = usePolyglot('card')
  const [collapsed, setCollapsed] = useState(startCollapsed)
  const [signed, setSigned] = useState(false)
  const [disabledEmailEditing, setDisabledEmailEditing] = useState(false)
  const [signatureProcess, setSignatureProcess] = useState(SIGNING_PROCESS.NOT_STARTED)
  const [errorsSignature, setErrorsSignature] = useState(null)
  const maxProgressRef = useRef(0)

  const [cmdStep, setCmdStep] = useState(CMD_STEP.IDLE)
  const [cmdPhone, setCmdPhone] = useState('')
  const [cmdPin, setCmdPin] = useState('')
  const [cmdOtp, setCmdOtp] = useState('')
  const [cmdInitData, setCmdInitData] = useState(null)
  const [otpTimeLeft, setOtpTimeLeft] = useState(60)
  const [cmdError, setCmdError] = useState(null)

  const sharedEmails = useMemo(() => getSharedEmails(prescription.sharedWith), [prescription.sharedWith])

  const [emails, setEmails] = useState(() => sharedEmails.map(toEmailEntry))
  // Nothing is rendered in the e-mail section until the suggested contacts arrive, otherwise an
  // empty field flashes on screen for the duration of the request.
  const [loadingContacts, setLoadingContacts] = useState(sharedEmails.length === 0)

  const loadContacts = useCallback(async () => {
    if (sharedEmails.length > 0) return
    let possibleEmails
    try {
      possibleEmails = await getContactsForPrescription(prescription.id)
    } catch (error) {
      console.error(error)
    }
    setEmails(Array.isArray(possibleEmails) && possibleEmails.length > 0
      ? possibleEmails.map(toEmailEntry)
      : [{ value: '', valid: false, confirmed: false }])
    setLoadingContacts(false)
  }, [prescription, sharedEmails, getContactsForPrescription, setEmails])

  const collapse = useCallback(() => setCollapsed(false), [])

  const internalSetSignatureProcess = useCallback((state, errors = null) => {
    setErrorsSignature(errors)
    setSignatureProcess(state)
    const progress = SIGNING_PROGRESS_TO_TEXT[state]?.progress ?? 0
    maxProgressRef.current = Math.max(maxProgressRef.current, progress)
  }, [])

  useEffect(() => {
    loadContacts().catch(console.error)
  }, [loadContacts])

  useEffect(() => {
    if (signatureProcess === SIGNING_PROCESS.PRESCRIBED) {
      setSigned(true)
      setErrorsSignature(null)
      setCmdStep(CMD_STEP.IDLE)
      setCmdInitData(null)
      collapse()
    }
  }, [signatureProcess, collapse])

  // OTP countdown timer — restarts each time we enter OTP state
  useEffect(() => {
    if (cmdStep !== CMD_STEP.OTP) return
    setOtpTimeLeft(60)
    const id = setInterval(() => {
      setOtpTimeLeft(prev => {
        if (prev <= 1) { clearInterval(id); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [cmdStep])

  const handleReset = useCallback(() => {
    setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
    setErrorsSignature(null)
    setCmdStep(CMD_STEP.IDLE)
    setCmdInitData(null)
    setCmdPhone('')
    setCmdPin('')
    setCmdOtp('')
    setCmdError(null)
    setDisabledEmailEditing(false)
    maxProgressRef.current = 0
  }, [])

  // ── Card signing ──────────────────────────────────────────────────────────
  const sign = useCallback(() => {
    setDisabledEmailEditing(true)
    addEmailsAndEmitPrescription(emails.map(em => em.value), internalSetSignatureProcess)
  }, [emails, addEmailsAndEmitPrescription, internalSetSignatureProcess])

  // ── CMD flow ──────────────────────────────────────────────────────────────
  const prescriptionName = useMemo(
    () => `Scribo | Receituário do(a) Dr(a) ${doctorClinicalName ?? ''} para ${patientFullName ?? ''}`.trim(),
    [doctorClinicalName, patientFullName]
  )

  const handlePhoneChange = useCallback((e) => {
    const value = e.target.value
    // Auto-format when user types exactly 9 raw digits (no country code)
    if (/^\d{9}$/.test(value)) {
      setCmdPhone(`+351 ${value}`)
    } else {
      setCmdPhone(value)
    }
  }, [])

  const handlePhoneBlur = useCallback(() => {
    const value = cmdPhone.trim()
    if (!value) return
    if (value.startsWith('+351') && !value.startsWith('+351 ')) {
      // +351 present but missing space — fix it
      setCmdPhone(`+351 ${value.slice(4).trim()}`)
    } else if (/^\d{9}$/.test(value)) {
      // 9 digits without country code
      setCmdPhone(`+351 ${value}`)
    }
  }, [cmdPhone])

  const handleCmdClick = useCallback(() => {
    setDisabledEmailEditing(true)
    setCmdStep(CMD_STEP.CREDENTIALS)
    setCmdError(null)
  }, [])

  const handleCmdCancel = useCallback(() => {
    setCmdStep(CMD_STEP.IDLE)
    setCmdInitData(null)
    setCmdPhone('')
    setCmdPin('')
    setCmdOtp('')
    setCmdError(null)
    setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
    setDisabledEmailEditing(false)
  }, [])

  const handleCmdSubmit = useCallback(async () => {
    if (!cmdPhone.trim() || !cmdPin.trim()) return
    setCmdStep(CMD_STEP.PROCESSING)
    setCmdError(null)
    try {
      const encPhone = await encryptWithAmaKey(cmdPhone.trim())
      const encPin = await encryptWithAmaKey(cmdPin.trim())
      setCmdPhone('')
      setCmdPin('')
      const result = await initiateCmdSignature(
        emails.map(e => e.value),
        encPhone,
        encPin,
        prescriptionName,
        internalSetSignatureProcess
      )
      if (!result) {
        // Error already surfaced via signatureProcess; user will use the retry button
        return
      }
      setCmdInitData({ ...result, encPhone, encPin })
      setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
      setCmdStep(CMD_STEP.OTP)
    } catch {
      setCmdError('Ocorreu um erro ao processar os dados. Tente novamente.')
      setCmdStep(CMD_STEP.CREDENTIALS)
      setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
    }
  }, [cmdPhone, cmdPin, emails, initiateCmdSignature, prescriptionName, internalSetSignatureProcess])

  const handleCmdResend = useCallback(async () => {
    if (!cmdInitData) return
    const { encPhone, encPin } = cmdInitData
    setCmdStep(CMD_STEP.PROCESSING)
    setCmdOtp('')
    setCmdError(null)
    try {
      const result = await initiateCmdSignature(
        emails.map(e => e.value),
        encPhone,
        encPin,
        prescriptionName,
        internalSetSignatureProcess
      )
      if (!result) return
      setCmdInitData({ ...result, encPhone, encPin })
      setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
      setCmdStep(CMD_STEP.OTP)
    } catch {
      setCmdError('Ocorreu um erro ao reenviar o código. Tente novamente.')
      setCmdStep(CMD_STEP.OTP)
      setSignatureProcess(SIGNING_PROCESS.NOT_STARTED)
    }
  }, [cmdInitData, emails, initiateCmdSignature, prescriptionName, internalSetSignatureProcess])

  const handleOtpSubmit = useCallback(async () => {
    if (!cmdOtp.trim() || !cmdInitData) return
    setCmdStep(CMD_STEP.OTP_PROCESSING)
    setCmdError(null)
    try {
      const encOtp = await encryptWithAmaKey(cmdOtp.trim())
      setCmdOtp('')
      const { id, processId, splitCertificates, token } = cmdInitData
      await completeCmdSignature(
        id, processId, encOtp, splitCertificates, token,
        emails.map(e => e.value),
        internalSetSignatureProcess
      )
      setCmdInitData(null)
    } catch {
      setCmdError('Ocorreu um erro ao verificar o código. Tente novamente.')
      setCmdStep(CMD_STEP.OTP)
    }
  }, [cmdOtp, cmdInitData, emails, completeCmdSignature, internalSetSignatureProcess])

  // ── Email management ──────────────────────────────────────────────────────
  const updateEmail = useCallback((index, newValue) => {
    setEmails(oldEmails => {
      const newEMails = [...oldEmails]
      // Editing an already confirmed e-mail puts it back into edit mode, so it has to be re-confirmed.
      newEMails[index] = { ...newEMails[index], value: newValue, valid: VALIDATE_EMAIL_REGEX.test(newValue), confirmed: false }
      return newEMails
    })
  }, [])

  const confirmEmail = useCallback(index => {
    setEmails(oldEmails => {
      const newEmails = [...oldEmails]
      newEmails[index] = { ...newEmails[index], confirmed: true }
      return newEmails
    })
  }, [])

  const removeEmail = useCallback(index => {
    setEmails(ems => [...ems.slice(0, index), ...ems.slice(index + 1)])
  }, [])

  const addEmail = useCallback(() => {
    setEmails(ems => [...ems, { value: '', valid: false, confirmed: false }])
  }, [])

  const allEmailsValid = useMemo(() => emails.length > 0 && !emails.find(e => !e.valid), [emails])
  const emailWarning = useMemo(() => {
    if (loadingContacts) return null
    if (emails.length === 0) return 'Adicione pelo menos um e-mail para poder assinar a prescrição.'
    if (!allEmailsValid) return 'Existem e-mails por preencher ou inválidos.'
    return null
  }, [emails, allEmailsValid, loadingContacts])
  const hasCmdKeys = useMemo(() => typeof cmdKeys !== 'number' || cmdKeys > 0, [cmdKeys])
  const signatureProgress = useMemo(() => SIGNING_PROGRESS_TO_TEXT[signatureProcess], [signatureProcess])
  const prescriptionCreatedAt = useMemo(() => ({ date: moment(prescription.createdAt).format('L-LTS') }), [prescription])

  return (
    <div className='prescription-card'>
      <span className='prescription-card-title'>{t('prescription.createdAt', prescriptionCreatedAt)}</span>

      {!signed && (
        <>
          <div className='email-section'>
            <span className='email-section-label'>Enviar para:</span>
            <div className='email-fields'>
              {loadingContacts && <div className='email-field-skeleton' aria-hidden='true' />}
              {!loadingContacts && emails.map((email, index) => (
                <EmailField
                  key={index}
                  value={email}
                  confirmed={email.confirmed}
                  setValue={disabledEmailEditing ? undefined : updateEmail.bind(null, index)}
                  isRemove={!disabledEmailEditing}
                  addButton={disabledEmailEditing ? null : removeEmail.bind(null, index)}
                  onConfirm={!email.confirmed ? confirmEmail.bind(null, index) : null}
                />
              ))}
            </div>
            {!loadingContacts && !disabledEmailEditing && emails.length < MAX_EMAILS && (
              <button type='button' className='add-email-btn' onClick={addEmail}>
                <span className='add-email-btn-icon'><FontAwesomeIcon icon={faPlus} /></span>
                <span className='add-email-btn-label'>Adicionar e-mail</span>
              </button>
            )}
          </div>

          <div className='card-table-section'>
            <div className='items-table-wrapper'>
              <table className='items-table'>
                <thead>
                  <tr>
                    <th>{t('prescription.labels.nameItem')}</th>
                    <th>{t('prescription.labels.packageItem')}</th>
                  </tr>
                </thead>
                <tbody>
                  <CardItems items={items} />
                </tbody>
              </table>
            </div>

            {signatureProcess === SIGNING_PROCESS.NOT_STARTED && cmdStep === CMD_STEP.IDLE && (
              <div className='sign-section'>
                {emailWarning && <span className='sign-note'>{emailWarning}</span>}
                <div className='sign-buttons'>
                  <button
                    type='submit'
                    className='btn-sign-card'
                    disabled={!shouldShowSignButton || !allEmailsValid}
                    onClick={sign}>
                    <FontAwesomeIcon icon={faAddressCard} />
                    <span>Assinar com Cartão de Cidadão</span>
                  </button>
                  <span
                    className='btn-sign-cmd-wrapper'
                    title={!hasCmdKeys ? 'Não tem assinaturas CMD disponíveis. Compre mais CMDs para usar este método.' : undefined}>
                    <button
                      type='button'
                      className='btn-sign-cmd'
                      disabled={!allEmailsValid || !hasCmdKeys}
                      onClick={handleCmdClick}>
                      <span className='cmd-icon' aria-hidden='true' style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/CMD_symbol.png)` }} />
                      <span>Assinar com CMD</span>
                    </button>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* CMD credentials form */}
          {cmdStep === CMD_STEP.CREDENTIALS && (
            <div className='cmd-form'>
              <span className='cmd-form-title'>Assinar com Chave Móvel Digital</span>
              <div className='cmd-form-row'>
                <div className='cmd-form-field'>
                  <label className='cmd-form-label'>Número de telemóvel</label>
                  <input
                    type='tel'
                    className='cmd-form-input'
                    value={cmdPhone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder='+351 9xx xxx xxx'
                    autoComplete='tel'
                  />
                </div>
                <div className='cmd-form-field'>
                  <label className='cmd-form-label'>PIN da Chave Móvel Digital</label>
                  <input
                    type='password'
                    className='cmd-form-input'
                    value={cmdPin}
                    onChange={e => setCmdPin(e.target.value.slice(0, 8))}
                    placeholder='••••••'
                    maxLength={8}
                    autoComplete='current-password'
                    onKeyDown={e => e.key === 'Enter' && handleCmdSubmit()}
                  />
                </div>
              </div>
              {cmdError && <span className='cmd-form-error'>{cmdError}</span>}
              <div className='cmd-form-actions'>
                <button type='button' className='btn-cmd-cancel' onClick={handleCmdCancel}>
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='btn-sign-cmd'
                  onClick={handleCmdSubmit}
                  disabled={!cmdPhone.trim() || !cmdPin.trim()}>
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* CMD OTP form */}
          {cmdStep === CMD_STEP.OTP && (
            <div className='cmd-form'>
              <span className='cmd-form-title'>Código de confirmação</span>
              <span className='cmd-form-subtitle'>
                Introduza o código OTP enviado para o seu telemóvel
              </span>
              <div className='cmd-form-field'>
                <label className='cmd-form-label'>Código OTP</label>
                <input
                  type='text'
                  className='cmd-form-input cmd-form-input--otp'
                  value={cmdOtp}
                  onChange={e => setCmdOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder='000000'
                  inputMode='numeric'
                  maxLength={6}
                  autoComplete='one-time-code'
                  onKeyDown={e => e.key === 'Enter' && handleOtpSubmit()}
                />
              </div>
              <div className='cmd-otp-timer'>
                <button
                  type='button'
                  className='cmd-resend-btn'
                  onClick={handleCmdResend}
                  disabled={otpTimeLeft > 0}
                >
                  {otpTimeLeft > 0
                    ? `Reenviar código (${otpTimeLeft}s)`
                    : 'Reenviar código'}
                </button>
              </div>
              {cmdError && <span className='cmd-form-error'>{cmdError}</span>}
              <div className='cmd-form-actions'>
                <button type='button' className='btn-cmd-cancel' onClick={handleCmdCancel}>
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='btn-sign-cmd'
                  onClick={handleOtpSubmit}
                  disabled={cmdOtp.trim().length < 4}>
                  Confirmar
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {signed && signatureProcess === SIGNING_PROCESS.NOT_STARTED && (
        <div className='sign-text'>Assinada</div>
      )}

      {signatureProcess !== SIGNING_PROCESS.NOT_STARTED && (
        <div className={`signature-section${signatureProcess === SIGNING_PROCESS.PRESCRIBED ? ' signature-section--success' : ''}`}>
          {signatureProcess === SIGNING_PROCESS.PRESCRIBED && (
            <div className='signature-success-icon'>
              <FontAwesomeIcon icon={faCircleCheck} />
            </div>
          )}
          <div className='signature-loader'>
            <div
              className={`signature-loader-fill${signatureProgress?.error ? ' signature-loader-fill--error' : ''}`}
              style={{ width: `${Math.max(signatureProgress.progress, maxProgressRef.current)}%` }}
            />
          </div>
          <div className='signature-text'>
            {signatureProgress.text.map((val, index) => (
              <span
                key={index}
                className={index === 0
                  ? `signature-title${signatureProgress?.error ? ' signature-title--error' : (signatureProcess === SIGNING_PROCESS.PRESCRIBED ? ' signature-title--success' : '')}`
                  : 'signature-subtitle'}
              >
                {t(`progress.${signatureProcess}.${val}`)}
              </span>
            ))}
          </div>
          {(signatureProcess % 2) !== 0 && (
            <div className='signature-retry'>
              <button type='button' className='btn-sign-cmd' onClick={handleReset}>
                {t('reset')}
              </button>
            </div>
          )}
          {signatureProgress?.external && Object.keys(errorsSignature).map(errorKey => (
            <SpmsProcessError errorKey={errorKey} key={errorKey} value={errorsSignature[errorKey].description} />
          ))}
        </div>
      )}
    </div>
  )
})

Card.propTypes = {
  number: PropTypes.number.isRequired,
  items: PropTypes.array.isRequired,
  shouldShowSignButton: PropTypes.bool.isRequired,
  prescription: PropTypes.object.isRequired,
  cmdKeys: PropTypes.number
}

export default Card
